import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Ban, CheckCircle2, ExternalLink, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCustomerView } from "@/context/CustomerViewContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { GovernedLink } from "./GovernedLink";
import { FOCUS_RING, StatusBadge, statusLabel } from "./Disclosure";

// BP3 structured transaction surface (COP-ACT-036..042, COP-UX): every consequential
// action is an inspectable prepared card. Execution happens ONLY via the governed
// confirm endpoint — the deterministic Connect service remains the executing
// authority. Voice may prepare; it can never confirm.
// V5-H4 (ACC-002/004/006, UX-010, ERR-003): keyboard-operable controls, icon+text status,
// predictable focus after confirm/discard/error, no dead controls.

const money = (v) => <span className="num whitespace-nowrap">${Number(v).toFixed(2)}</span>;

function Row({ label, children, testid }) {
  return (
    <p className="break-words text-xs text-charcoal" data-testid={testid}>
      <span className="text-warmgrey">{label}: </span>{children}
    </p>
  );
}

function Facts({ card }) {
  const f = card.facts || {};
  const kind = card.action_kind;
  if (kind === "sample_request") {
    return (
      <div className="mt-1.5 space-y-1" data-testid="copilot-action-facts">
        {(f.lines || []).map((l, i) => (
          <p key={i} className="break-words text-xs text-charcoal">
            {l.label} — <span className="num whitespace-nowrap">{l.quantity} {l.quantity_unit}</span>
            {l.charged_quantity > 0
              ? <span className="text-warmgrey"> ({l.free_quantity} free · {l.charged_quantity} chargeable{l.line_charge != null ? <> · {money(l.line_charge)}</> : ""})</span>
              : <span className="text-warmgrey"> (within free allowance)</span>}
          </p>
        ))}
        {f.allowance && (
          <Row label="Allowance">{f.allowance.remaining_before_request} of {f.allowance.free_per_month} free unit(s) left this month</Row>
        )}
        {f.charge_total != null && (
          <Row label="Total due">{f.charge_total > 0 ? <span className="font-semibold">{money(f.charge_total)}</span> : "No charge due"}</Row>
        )}
        {f.destination && (
          <Row label="Ship to">{[f.destination.name, f.destination.line1, f.destination.city, f.destination.state, f.destination.postal_code].filter(Boolean).join(", ")}{f.destination.direct_to_client ? " (address given for this transaction)" : f.destination_source === "employee_showroom" ? ` (your assigned showroom${f.destination_label ? ` · ${f.destination_label}` : ""} — tell ZELI a different address to override)` : " (dealer default location)"}</Row>
        )}
        {f.destination?.phone && <Row label="Recipient phone">{f.destination.phone}</Row>}
        {f.destination?.email && <Row label="Recipient email">{f.destination.email}</Row>}
        {f.shipping_status && <Row label="Shipping">{f.shipping_status}</Row>}
        {f.dealer_email && <Row label="Dealer email"><span data-testid="copilot-action-dealer-email">{f.dealer_email}</span> ({f.dealer_email_source === "profile_verified_email" ? "your verified business e-mail" : "stated for this transaction"})</Row>}
        {f.project && <Row label="Project">{f.project.name}</Row>}
        {f.custom_note && <p className="break-words text-xs text-amber-700" data-testid="copilot-action-custom-note">{f.custom_note}</p>}
        <p className="text-[10px] text-warmgrey">{f.allowance_note} {f.payment_note}</p>
      </div>
    );
  }
  if (kind === "reservation") {
    return (
      <div className="mt-1.5 space-y-1" data-testid="copilot-action-facts">
        <Row label="Product">{f.label}</Row>
        <Row label="Quantity"><span className="num whitespace-nowrap">{f.boxes} boxes</span> · <span className="num whitespace-nowrap">{f.sqft} sq ft</span></Row>
        <Row label="Supply">{f.supply?.category}{f.supply?.container_number ? ` · container ${f.supply.container_number}` : ""}</Row>
        <Row label="Hold">{f.hold_type === "repeat" ? "Repeat hold — pending Zelligio approval" : `First hold — ACTIVE for ${f.hold_hours}h`}</Row>
        <Row label="Location">{f.location_code || "FLORIDA"}</Row>
        {f.expires_preview && <Row label="Expires">{new Date(f.expires_preview).toLocaleString()} (if confirmed now)</Row>}
        {f.availability_boxes != null && <Row label="Availability now"><span className="num whitespace-nowrap">{f.availability_boxes} boxes</span></Row>}
        {f.project && <Row label="Project">{f.project.name}</Row>}
        {f.shortfall_note && <p className="break-words text-xs text-amber-700" data-testid="copilot-action-shortfall">{f.shortfall_note}</p>}
        <p className="text-[10px] text-warmgrey">{f.hold_note} {f.availability_note}</p>
      </div>
    );
  }
  if (kind === "order") {
    return (
      <div className="mt-1.5 space-y-1" data-testid="copilot-action-facts">
        {(f.lines || []).map((l, i) => (
          <p key={i} className="break-words text-xs text-charcoal">
            {l.label} — <span className="num whitespace-nowrap">{l.boxes} boxes</span>
            {l.trade_price_per_box != null && <> · <span className="num whitespace-nowrap">${Number(l.trade_price_per_box).toFixed(2)}/box</span></>}
            {l.line_subtotal != null && <> = <span className="font-medium">{money(l.line_subtotal)}</span></>}
            {l.source === "RESERVATION" && <span className="text-warmgrey"> (from reservation)</span>}
          </p>
        ))}
        {f.subtotal_usd != null && <Row label="Subtotal">{money(f.subtotal_usd)}</Row>}
        {f.freight_usd != null && <Row label="Freight">{money(f.freight_usd)}{f.freight_status ? ` (${f.freight_status})` : ""}</Row>}
        {f.grand_total_usd != null && <Row label="Total"><span className="font-semibold" data-testid="copilot-action-total">{money(f.grand_total_usd)}</span></Row>}
        {f.ship_to && (
          <Row label="Ship to">{[f.ship_to.name, f.ship_to.line1, f.ship_to.city, f.ship_to.state, f.ship_to.postal_code].filter(Boolean).join(", ")}{f.ship_to.source === "transaction_address" ? " (address given for this transaction)" : f.ship_to.source === "employee_showroom" ? ` (your assigned showroom${f.ship_to.label ? ` · ${f.ship_to.label}` : ""} — tell ZELI a different address to override)` : " (dealer default location)"}</Row>
        )}
        {f.ship_to?.email && <Row label="Client email">{f.ship_to.email}{f.ship_to.notify_client ? " · shipping notification authorized" : " · no client notification"}</Row>}
        {f.confirmation_requirement && <Row label="Confirmation">{f.confirmation_requirement}</Row>}
        {f.dealer_email && <Row label="Dealer email"><span data-testid="copilot-action-dealer-email">{f.dealer_email}</span> ({f.dealer_email_source === "profile_verified_email" ? "your verified business e-mail" : "stated for this transaction"})</Row>}
        {f.dc_terms && (
          <Row label="Direct-container terms">30% deposit {money(f.dc_terms.deposit_amount_usd)} · 70% balance {money(f.dc_terms.balance_amount_usd)}</Row>
        )}
        <p className="text-[10px] text-warmgrey">{f.tax_note}</p>
        <p className="text-[10px] text-warmgrey">{f.payment_note}</p>
      </div>
    );
  }
  if (kind === "sourcing_request") {
    return (
      <div className="mt-1.5 space-y-1" data-testid="copilot-action-facts">
        <Row label="Product">{f.label || f.sku}{f.format_in ? ` · ${f.format_in}` : ""}</Row>
        <Row label="Quantity"><span className="num whitespace-nowrap">{f.boxes} boxes</span>{f.sqft != null ? <> · <span className="num whitespace-nowrap">{f.sqft} sq ft</span></> : null}</Row>
        {f.ship_to && <Row label="Ship to">{[f.ship_to.name, f.ship_to.line1, f.ship_to.city, f.ship_to.state, f.ship_to.postal_code].filter(Boolean).join(", ") || f.ship_to.label || "—"}</Row>}
        {f.project && <Row label="Project">{f.project.name}</Row>}
        {f.timing && <Row label="Timing">{f.timing.display || f.timing.raw || String(f.timing)}</Row>}
        {f.payment_terms && <Row label="Payment terms">{f.payment_terms}</Row>}
        {f.notes && <Row label="Notes">{f.notes}</Row>}
        <p className="mt-1 break-words rounded-sm bg-sand/70 p-2 text-xs text-charcoal" data-testid="copilot-action-consequence">{f.consequence}</p>
      </div>
    );
  }
  if (kind === "custom_studio_request") {
    return (
      <div className="mt-1.5 space-y-1" data-testid="copilot-action-facts">
        <Row label="Design">{f.design_name}</Row>
        {(f.lines || []).map((l, i) => (
          <p key={i} className="break-words text-xs text-charcoal" data-testid={`copilot-custom-line-${i}`}>
            <span className="text-warmgrey">Line {i + 1}: </span>{l.summary}
            {l.composition && l.composition !== "SOLID" && <span className="text-warmgrey"> · {l.composition.replaceAll("_", "-").toLowerCase()}</span>}
            {l.notes && <span className="text-warmgrey"> · {l.notes}</span>}
          </p>
        ))}
        {f.line_totals && <Row label="Governed total"><span className="num whitespace-nowrap">{f.line_totals.total_sqft} sq ft</span> across {f.lines?.length} line(s)</Row>}
        {f.moq?.message && <p className="break-words text-xs text-amber-700" data-testid="copilot-custom-moq">{f.moq.message}</p>}
        {(f.project || f.new_project_name) && <Row label="Project">{f.project ? f.project.name : `${f.new_project_name} (new)`}</Row>}
        {(f.attachments || []).length > 0 && <Row label="Design files">{f.attachments.map((a) => a.filename).join(", ")}</Row>}
        {f.instructions && <Row label="Instructions">{f.instructions}</Row>}
        <p className="mt-1 break-words rounded-sm bg-sand/70 p-2 text-xs text-charcoal" data-testid="copilot-action-consequence">{f.consequence}</p>
      </div>
    );
  }
  // cancellation kinds
  return (
    <div className="mt-1.5 space-y-1" data-testid="copilot-action-facts">
      {f.label && <Row label="Item">{f.label}{f.boxes != null ? ` — ${f.boxes} boxes` : ""}</Row>}
      {f.order_number && <Row label="Order">{f.order_number}</Row>}
      {(f.lines || []).map((l, i) => (
        <p key={i} className="break-words text-xs text-charcoal">{l.label} — {l.quantity} unit(s){l.shipped_quantity ? ` (${l.shipped_quantity} shipped)` : ""}</p>
      ))}
      <Row label="Current state">{f.status}{f.window ? ` · ${f.window.replaceAll("_", " ")}` : ""}</Row>
      {f.deposit && <Row label="Deposit">{f.deposit.status}{f.deposit.amount_usd != null ? <> · {money(f.deposit.amount_usd)}</> : ""}</Row>}
      <p className="mt-1 break-words rounded-sm bg-sand/70 p-2 text-xs text-charcoal" data-testid="copilot-action-consequence">{f.consequence}</p>
    </div>
  );
}

const BTN = `inline-flex min-h-[36px] items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${FOCUS_RING}`;

export function ActionCard({ card, onPatch, onResult, onAnnounce, onFocusComposer }) {
  const { customerView } = useCustomerView();
  const { user: authUser } = useAuth() || {};
  // V5.1 RC51-ROL-M04: UI parity with server-side role rules — Sales/Designer may prepare but not submit orders/sourcing (CAP-33/37);
  // quote approval is Admin / own-location Manager only (CAP-23). Samples and reservations stay available to Designers (CAP-27/30).
  const designer = authUser?.dealer_role === "SALES_DESIGNER";
  const roleBlocked = card.status === "PREPARED" && (
    (designer && ["order", "order_cancel", "sourcing_request"].includes(card.action_kind)) ? "Prepared for review — only your Dealer Admin or Location Manager can confirm orders and sourcing requests." :
    (designer && card.action_kind === "quote_approval") ? "Prepared for review — only a Dealer Admin or authorized Location Manager can decide pricing exceptions." : null);
  const [busy, setBusy] = useState(false);
  const [lastError, setLastError] = useState(null);
  const outcomeRef = useRef(null);
  const errorRef = useRef(null);
  const pendingFocusRef = useRef(null);  // "outcome" | "error" | "composer"
  const r = card.result || {};

  // ACC-006: focus moves only after the authoritative status is rendered — never before.
  useEffect(() => {
    const want = pendingFocusRef.current;
    if (!want) return;
    if (want === "outcome" && outcomeRef.current && card.status !== "PREPARED") {
      outcomeRef.current.focus();
      pendingFocusRef.current = null;
    } else if (want === "error" && errorRef.current) {
      errorRef.current.focus();
      pendingFocusRef.current = null;
    } else if (want === "composer") {
      onFocusComposer?.();
      pendingFocusRef.current = null;
    }
  }, [card.status, lastError, onFocusComposer]);

  const announce = (text) => onAnnounce?.(text);

  const confirm = async () => {
    if (busy || card.status !== "PREPARED") return;
    setBusy(true);
    setLastError(null);
    try {
      const res = await api.post(`/copilot/actions/${card.action_id}/confirm`);
      if (res.data?.action) {
        onPatch(card.action_id, res.data.action);
        if (res.data.action.status !== "PREPARED") {
          pendingFocusRef.current = "outcome";
          announce(`${card.title}: ${res.data.action.status_label || statusLabel(res.data.action.status)}.`);
        }
      }
      if (res.data?.changed) {
        toast.info("Details changed since preparation — review the refreshed facts, then confirm again.");
        announce("Details changed since preparation — review the refreshed facts, then confirm again.");
      } else if (res.data?.result_message) {
        onResult(res.data.result_message);
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = (d && d.message) || "The action could not be executed — nothing was changed.";
      setLastError(msg);
      toast.error(msg);
      if (d?.action) onPatch(card.action_id, d.action);
      pendingFocusRef.current = d?.action && d.action.status !== "PREPARED" ? "outcome" : "error";
      if (d?.code === "not_eligible" || d?.code === "state_changed") onResult(null);
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    if (busy || card.status !== "PREPARED") return;
    setBusy(true);
    try {
      const res = await api.post(`/copilot/actions/${card.action_id}/discard`);
      if (res.data?.action) onPatch(card.action_id, res.data.action);
      announce(`${card.title}: Discarded — nothing was executed.`);
      pendingFocusRef.current = "composer";
    } catch {
      toast.error("Couldn't discard right now — the action expires on its own and nothing was executed.");
    } finally {
      setBusy(false);
    }
  };

  // ACC-002: Escape discards ONLY a focused PREPARED card (not from inputs / modals); Escape never confirms.
  const onKeyDown = (e) => {
    if (e.key !== "Escape" || card.status !== "PREPARED" || busy || customerView) return;
    const tag = e.target?.tagName;
    if (tag === "TEXTAREA" || tag === "INPUT" || tag === "SELECT") return;
    if (!e.currentTarget.contains(document.activeElement)) return;
    e.preventDefault();
    e.stopPropagation();
    discard();
  };

  const openLabel = r.collection === "orders" ? "Orders" : r.collection === "reservations" ? "Reservations" : "Samples";

  return (
    <div className={`rounded-md border border-terracotta/40 bg-white p-3 ${FOCUS_RING}`} data-testid="copilot-action-card" data-action-id={card.action_id}
      data-action-kind={card.action_kind} data-action-status={card.status} tabIndex={card.status === "PREPARED" ? 0 : -1} onKeyDown={onKeyDown}
      aria-label={`${card.title} — ${card.status_label || statusLabel(card.status)}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 break-words text-xs font-semibold uppercase tracking-wide text-terracotta">{card.title}</p>
        <StatusBadge status={card.status} label={card.status_label} />
      </div>
      {card.confirmation_class != null && (
        <p className="mt-1 text-[10px] uppercase tracking-wide text-warmgrey" data-testid="copilot-action-class" data-class={card.confirmation_class}>
          {card.confirmation_class === 1 ? "Draft · not submitted" : card.confirmation_class === 3 ? "Class 3 · explicit confirmation of commercial terms required" : "Class 2 · confirmation required"}
          {card.supersedes ? " · replaces earlier preparation" : ""}
        </p>
      )}
      {card.status === "CONFIRMED_AWAITING_EXECUTION" && (
        <p ref={outcomeRef} tabIndex={-1} className={`mt-2 text-xs text-charcoal ${FOCUS_RING}`} data-testid="copilot-action-deferred">Confirmed and recorded — the request itself is submitted through the deterministic Connect workflow. Nothing has been sent yet.</p>
      )}
      {(card.actions || []).some((a) => a.kind === "link") && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid="copilot-action-links">
          {card.actions.filter((a) => a.kind === "link").map((a, i) => (
            <GovernedLink key={i} target={a.target} className={`rounded-full border border-terracotta/40 px-3 py-1 text-[11px] font-medium text-terracotta transition-colors hover:bg-terracotta hover:text-white ${FOCUS_RING}`}>{a.label}</GovernedLink>
          ))}
        </div>
      )}
      {card.refreshed && card.status === "PREPARED" && (
        <p className="mt-1.5 flex items-start gap-1.5 rounded-sm bg-amber-50 p-2 text-xs text-amber-800" role="status" data-testid="copilot-action-refreshed">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" /> <span><span className="font-medium">Changed:</span> details changed since preparation — review the refreshed facts before confirming.</span>
        </p>
      )}
      <Facts card={card} />

      {card.status === "PREPARED" && lastError && (
        <p ref={errorRef} tabIndex={-1} role="alert" className={`mt-2 flex items-start gap-1.5 break-words rounded-sm bg-red-50 p-2 text-xs text-red-700 ${FOCUS_RING}`} data-testid="copilot-action-error">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" /> <span><span className="font-medium">Error:</span> {lastError} You can confirm again or discard.</span>
        </p>
      )}

      {card.status === "PREPARED" && (
        <>
          <p className="mt-2 text-[10px] italic text-warmgrey">{card.note}</p>
          {customerView ? (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-warmgrey" data-testid="copilot-action-mode-blocked">
              <Ban className="h-3 w-3" aria-hidden="true" /> Switch back to Trade Mode to review economics and confirm this action.
            </p>
          ) : roleBlocked ? (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-warmgrey" data-testid="copilot-action-role-blocked">
              <Ban className="h-3 w-3" aria-hidden="true" /> {roleBlocked}
            </p>
          ) : (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={confirm}
                disabled={busy}
                aria-disabled={busy}
                aria-busy={busy}
                className={`${BTN} bg-terracotta text-white hover:bg-terracotta-hover disabled:opacity-60`}
                data-testid="copilot-action-confirm"
              >
                {busy ? <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden="true" /> : <CheckCircle2 className="h-3 w-3" aria-hidden="true" />}
                {busy ? "Working…" : "Confirm — execute via Connect"}
              </button>
              <button
                type="button"
                onClick={discard}
                disabled={busy}
                aria-disabled={busy}
                className={`${BTN} border border-border text-warmgrey hover:border-red-200 hover:text-red-600 disabled:opacity-60`}
                data-testid="copilot-action-discard"
              >
                Discard
              </button>
              <span className="sr-only">Press Escape while this card is focused to discard it. Escape never confirms.</span>
            </div>
          )}
        </>
      )}

      {card.status === "EXECUTED" && r && (
        <div ref={outcomeRef} tabIndex={-1} className={`mt-2 space-y-1 border-t border-border pt-2 ${FOCUS_RING}`} role="status" data-testid="copilot-action-result">
          <p className="flex items-start gap-1.5 break-words text-xs font-medium text-charcoal">
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-600" aria-hidden="true" />
            <span><span className="font-semibold">Executed:</span> {r.order_number ? `Order ${r.order_number}` : r.label || "Record"} — <span className="num">{r.status}</span>
            {r.cancellation_request_status ? ` · cancellation ${r.cancellation_request_status.toLowerCase()}` : ""}</span>
          </p>
          {r.refund_status && <p className="text-xs text-charcoal">Refund {r.refund_status} (provider-confirmed state).</p>}
          {r.checkout_url && (
            <a href={r.checkout_url} target="_blank" rel="noreferrer"
               className={`inline-flex items-center gap-1 text-xs font-medium text-terracotta hover:underline ${FOCUS_RING}`}
               data-testid="copilot-action-payment-link">
              Complete payment in the governed checkout <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </a>
          )}
          {r.route && r.id ? (
            <Link to={r.collection === "orders" ? `${r.route}/${r.id}` : r.route}
                  className={`inline-flex items-center gap-1 text-xs font-medium text-terracotta hover:underline ${FOCUS_RING}`}
                  data-testid="copilot-action-open">
              Open in {openLabel}
            </Link>
          ) : (
            <p className="text-[11px] text-warmgrey" data-testid="copilot-action-open-unavailable">Record link not available — the record exists in Connect; open the module from the menu.</p>
          )}
          <p className="text-[10px] text-warmgrey" data-testid="copilot-action-provenance">
            Prepared by ZELI · confirmed by you · executed by the deterministic Connect service
          </p>
        </div>
      )}

      {card.status === "FAILED" && (
        <p ref={outcomeRef} tabIndex={-1} role="alert" className={`mt-2 flex items-start gap-1.5 break-words text-xs text-red-700 ${FOCUS_RING}`} data-testid="copilot-action-failed">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
          <span><span className="font-medium">Failed:</span> {r.detail || "The deterministic service rejected this action — nothing was executed."}</span>
        </p>
      )}
      {card.status === "DISCARDED" && (
        <p ref={outcomeRef} tabIndex={-1} className={`mt-2 text-xs text-warmgrey ${FOCUS_RING}`} data-testid="copilot-action-discarded">Discarded — nothing was executed.</p>
      )}
      {card.status === "EXPIRED" && (
        <p ref={outcomeRef} tabIndex={-1} className={`mt-2 text-xs text-warmgrey ${FOCUS_RING}`} data-testid="copilot-action-expired">Expired — nothing was executed. Ask ZELI again to prepare a fresh action.</p>
      )}
    </div>
  );
}
